import { CartContainerItemAddInput } from '@/features/graphql/generated/graphql'
import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import { cartContainerItemAddMutation } from '@/features/pos/components/Cart/mutations'
import {
    getCartFromCache,
    writeCartContainerItemsToCache,
    writeCartQueryToCache,
} from '@/features/pos/components/Cart/utils/cart-cache'
import { appendCartContainerItem } from '@/features/pos/components/Cart/utils/cart-container-item'
import { logger } from '@/features/pos/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useMutation } from '@apollo/client'
import { useCallback } from 'react'

export const useAddCartContainerItem = (): ((
    item: CartContainerItemAddInput
) => Promise<boolean>) => {
    const { cartKey, setCartKey } = useCartKeyContext()

    const handleError = useHandleErrorWithToast(logger)

    const [cartContainerItemAdd] = useMutation(cartContainerItemAddMutation, {
        fetchPolicy: 'no-cache',
        update: (cache, { data }) => {
            const resCartKey = data?.cartContainerItemAdd?.key
            const resItems = data?.cartContainerItemAdd?.items
            const resContainerItems = data?.cartContainerItemAdd?.containerItems

            if (!resCartKey || !resItems || !resContainerItems) return

            if (resCartKey !== cartKey) {
                setCartKey(resCartKey)

                return writeCartQueryToCache(cache, resCartKey, resItems, resContainerItems)
            }

            const currentCart = getCartFromCache(cache, cartKey)
            const newItem = resContainerItems[resContainerItems.length - 1]

            writeCartContainerItemsToCache(
                cache,
                resCartKey,
                appendCartContainerItem(currentCart?.containerItems, newItem)
            )
        },
        onError: handleError,
    })

    return useCallback(
        async (item: CartContainerItemAddInput) => {
            const { data } = await cartContainerItemAdd({
                variables: { containerItemAdd: item, cartKey },
            })

            return Boolean(data?.cartContainerItemAdd)
        },
        [cartKey, cartContainerItemAdd]
    )
}
